import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

export const HIGHLIGHT_DURATION_MS = 2000;

/**
 * If the row is the active deep-link target at first mount (i.e. arrived via deep
 * link or refresh), flash + scroll it into view for HIGHLIGHT_DURATION_MS so
 * the user can spot it. Subsequent isActive transitions don't trigger another
 * flash — clicking a row that's already in view doesn't need a flash to find it,
 * and the target-id being cleared from the URL after consumption must not cancel
 * an in-progress flash.
 *
 * `behavior` defaults to 'smooth'. Pass 'auto' (instant) when the page strips the
 * deep-link param on consumption: that re-render cancels an in-flight smooth scroll,
 * leaving the row off-screen — an instant scroll lands before the re-render.
 */
export default function useDeepLinkHighlight(
    isActive: boolean,
    ref: RefObject<HTMLElement | null>,
    behavior: ScrollBehavior = 'smooth'
): boolean {
    const [shouldFlash, setShouldFlash] = useState(() => isActive);

    useEffect(() => {
        if (!shouldFlash) {
            return undefined;
        }
        ref.current?.scrollIntoView({ behavior, block: 'center' });
        const timer = window.setTimeout(() => setShouldFlash(false), HIGHLIGHT_DURATION_MS);
        return () => window.clearTimeout(timer);
    }, [shouldFlash, ref, behavior]);

    return shouldFlash;
}
